<template>
  <header class="header" ref="headerRef">
    <div class="header-container">
      <div class="logo">
        <router-link :to="{ name: 'Home' }">
          <img src="@/assets/logo.png" alt="logo" class="logo-img" />
        </router-link>
      </div>
      <nav class="nav">
        <ul>
          <li><router-link :to="{ name: 'Home' }" exact-active-class="active">首页</router-link></li>
          <li><router-link :to="{ name: 'Products' }" active-class="active">产品中心</router-link></li>
          <li><router-link :to="{ name: 'About' }" active-class="active">关于我们</router-link></li>
          <li><router-link :to="{ name: 'Articles' }" active-class="active">推荐文章</router-link></li>
        </ul>
      </nav>
      <div class="search" ref="searchRef">
        <input 
          type="text" 
          placeholder="输入药品名称、与家或批准文号搜索" 
          v-model="searchKeyword" 
          @keyup.enter="searchMedicine"
        />
        <button type="button" @click="searchMedicine">
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
          搜索
        </button>
        
        <!-- 搜索结果下拉框 -->
        <div class="search-results" v-if="showResults">
          <div v-if="loading" class="loading-results">
            <span>搜索中...</span>
          </div>
          <div v-else-if="error" class="error-results">
            <span>{{ error }}</span>
          </div>
          <div v-else-if="searchResults.length === 0" class="empty-results">
            <span>未找到相关药品</span>
          </div>
          <ul v-else>
            <li v-for="item in searchResults" :key="item.id" @click="goToDetail(item.id)">
              <div class="search-item">
                <div class="search-item-image">
                  <img :src="getFullImageUrl(item.image) || defaultImage" :alt="item.name" />
                </div>
                <div class="search-item-info">
                  <div class="search-item-name">{{ item.name }}</div>
                  <div class="search-item-manufacturer">{{ item.manufacturer }}</div>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
      
      <!-- 用户账户区域 -->
      <div class="user-account">
        <div v-if="isLoggedIn" class="user-menu" ref="userMenuRef">
          <div class="user-info" @click="showUserMenu = !showUserMenu">
            <div class="user-avatar">{{ userInitial }}</div>
            <span class="user-name">{{ userName }}</span>
            <svg class="dropdown-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M7 10l5 5 5-5z"/></svg>
          </div>
          
          <!-- 用户下拉菜单 -->
          <div v-if="showUserMenu" class="user-dropdown">
            <div class="dropdown-item logout" @click="handleLogout">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"><path d="M17 7l-1.41 1.41L18.17 11H8v2h10.17l-2.58 2.58L17 17l5-5zM4 5h8V3H4c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h8v-2H4V5z"/></svg>
              退出登录
            </div>
          </div>
        </div>
        
        <button v-else class="auth-btn" @click="openAuthModal">
          登录/注册
        </button>
      </div>
    </div>
  </header>
  
  <!-- 登录注册弹窗 -->
  <AuthModal 
    :isOpen="authModalOpen" 
    @close="authModalOpen = false"
    @login-success="handleLoginSuccess"
    @register-success="handleRegisterSuccess"
  />
  
  <!-- 支付二维码弹窗 -->
  <div v-if="paymentDialogVisible" class="payment-dialog-overlay" @click="closePaymentDialog">
    <div class="payment-dialog" @click.stop>
      <div class="payment-header">
        <h3>付费查看详情</h3>
        <button class="close-btn" @click="closePaymentDialog">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
        </button>
      </div>
      <div class="payment-content">
        <div class="product-info">
          <h4>{{ currentProduct.name }}</h4>
          <p class="price">¥ {{ currentPrice }}</p>
        </div>
        <div class="qr-code-container">
          <img src="@/assets/zhifu.png" alt="支付二维码" class="qr-code" />
          <p class="qr-tip">扫码支付后即可查看完整详情</p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ElMessage } from 'element-plus';
import { searchMedicineAPI, getPermittedMedicines, BASE_URL } from '@/api/config';
import { getToken, isLoggedIn } from '@/utils/userStore';
import AuthModal from './AuthModal.vue';

export default {
  name: 'TheHeader',
  components: {
    AuthModal
  },
  data() {
    return {
      searchKeyword: '',
      searchResults: [],
      showResults: false,
      loading: false,
      error: null,
      defaultImage: 'https://via.placeholder.com/50x50?text=药品',
      authModalOpen: false,
      isLoggedIn: false,
      userInfo: null,
      showUserMenu: false,
      
      // 支付弹窗相关数据
      paymentDialogVisible: false,
      currentProduct: {},
      currentPrice: 0,
      
      // 权限药品列表
      permittedMedicineIds: []
    };
  },
  computed: {
    userName() {
      return this.userInfo?.nickname || this.userInfo?.username || '用户';
    },
    userInitial() {
      const name = this.userName;
      return name.charAt(0).toUpperCase();
    }
  },
  methods: {
    async searchMedicine() {
      // 如果搜索关键词为空，不执行搜索
      if (!this.searchKeyword.trim()) {
        this.error = '请输入搜索关键词';
        this.showResults = true;
        return;
      }
      
      this.loading = true;
      this.error = null;
      this.showResults = true;
      
      try {
        const response = await searchMedicineAPI(this.searchKeyword);
        
        if (response.code === 1) {
          this.searchResults = response.data.data;
        } else {
          this.error = response.msg;
          this.searchResults = [];
        }
      } catch (error) {
        console.error('搜索失败:', error);
        this.error = '搜索失败，请稍后重试';
        this.searchResults = [];
      } finally {
        this.loading = false;
      }
    },
    
    async goToDetail(id) {
      // 关闭SearchResults
      this.showResults = false;
      
      // 找到对应的产品
      const product = this.searchResults.find(item => item.id === id);
      if (!product) {
        this.$router.push({ name: 'ProductDetail', params: { id } });
        return;
      }
      
      // 检查用户是否已登录
      if (!isLoggedIn()) {
        // 未登录，昺示支付弹窗
        this.currentProduct = product;
        this.currentPrice = product.price || 0;
        this.paymentDialogVisible = true;
        return;
      }
      
      // 已登录，检查是否有权限
      if (this.isProductPermitted(product.id)) {
        // 有权限，跳转到详情页
        this.$router.push({ name: 'ProductDetail', params: { id } });
      } else {
        // 没有权限，昺示支付弹窗
        this.currentProduct = product;
        this.currentPrice = product.price || 0;
        this.paymentDialogVisible = true;
      }
    },
    
    handleClickOutside(event) {
      // 检查搜索结果是否需要关闭
      if (this.$refs.searchRef && !this.$refs.searchRef.contains(event.target)) {
        this.showResults = false;
      }
      
      // 检查用户菜单是否需要关闭
      if (this.$refs.userMenuRef && !this.$refs.userMenuRef.contains(event.target)) {
        this.showUserMenu = false;
      }
    },
    
    openAuthModal() {
      this.authModalOpen = true;
    },
    
    async handleLoginSuccess(userInfo) {
      this.isLoggedIn = true;
      this.userInfo = userInfo;
      this.showUserMenu = false;
      
      // 加载权限列表
      await this.loadPermittedMedicines();
      
      // 刷新页面以更新权限状态
      this.$nextTick(() => {
        window.location.reload();
      });
    },
    
    async handleRegisterSuccess(userInfo) {
      this.isLoggedIn = true;
      this.userInfo = userInfo;
      this.showUserMenu = false;
      
      // 加载权限列表
      await this.loadPermittedMedicines();
      
      // 刷新页面以更新权限状态
      this.$nextTick(() => {
        window.location.reload();
      });
    },
    
    handleLogout() {
      localStorage.removeItem('token');
      localStorage.removeItem('userInfo');
      this.isLoggedIn = false;
      this.userInfo = null;
      this.showUserMenu = false;
      this.permittedMedicineIds = []; // 清空权限列表
      
      ElMessage({
        message: '已退出登录',
        type: 'success'
      });
      
      // 刷新页面以清除权限状态
      this.$nextTick(() => {
        window.location.reload();
      });
    },
    
    // 检查药品是否有权限
    isProductPermitted(productId) {
      return this.permittedMedicineIds.includes(productId);
    },
    
    // 加载权限药品列表
    async loadPermittedMedicines() {
      if (!isLoggedIn()) {
        this.permittedMedicineIds = [];
        return;
      }
      
      try {
        const token = getToken();
        const response = await getPermittedMedicines(token, 1, 100);
        
        if (response.code === 1 && response.data && response.data.data) {
          this.permittedMedicineIds = response.data.data.map(item => item.id);
        }
      } catch (error) {
        console.error('加载权限药品列表失败:', error);
      }
    },
    
    // 关闭支付弹窗
    closePaymentDialog() {
      this.paymentDialogVisible = false;
      this.currentProduct = {};
      this.currentPrice = 0;
    },
    
    // 获取完整图片URL
    getFullImageUrl(path) {
      if (!path) return null;
      
      // 如果已经是完整URL则直接返回
      if (path.startsWith('http://') || path.startsWith('https://')) {
        return path;
      }
      
      // 否则拼接基础URL
      return `${BASE_URL}${path}`;
    },
    
    checkLoginStatus() {
      const token = localStorage.getItem('token');
      const userInfoStr = localStorage.getItem('userInfo');
      
      if (token && userInfoStr) {
        try {
          this.userInfo = JSON.parse(userInfoStr);
          this.isLoggedIn = true;
          // 登录后加载权限列表
          this.loadPermittedMedicines();
        } catch (error) {
          console.error('解析用户信息失败:', error);
          localStorage.removeItem('token');
          localStorage.removeItem('userInfo');
        }
      }
    }
  },
  mounted() {
    // 添加点击外部关闭SearchResults的事件监听
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.boundHandleClickOutside);
    // 检查登录状态
    this.checkLoginStatus();
  },
  beforeUnmount() {
    // 移除事件监听
    if (this.boundHandleClickOutside) {
      document.removeEventListener('click', this.boundHandleClickOutside);
    }
  }
}
</script>

<style scoped>
/* 顶部导航组件样式 */
.header {
  background: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  transition: all 0.3s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.3);
}

@keyframes gradient-animation {
  0% {
    background-position: 0% 50%;
  }
  50% {
    background-position: 100% 50%;
  }
  100% {
    background-position: 0% 50%;
  }
}

.header-container {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 18px 20px 10px 20px;
}

.logo h1 {
  font-size: 1.8rem;
  color: #3a8ee6;
  margin: 0;
}

.logo-img {
  height: 48px;
  width: auto;
  display: block;
  filter: drop-shadow(0 2px 6px rgba(58,142,230,0.12));
}

.nav ul {
  display: flex;
  list-style-type: none;
  gap: 36px;
}

.nav li {
  position: relative;
}

.nav a {
  text-decoration: none;
  color: #333;
  font-weight: 500;
  font-size: 1.05rem;
  padding: 6px 0;
  position: relative;
  transition: color 0.3s;
  letter-spacing: 0.2px;
}

.nav a:hover, .nav a.active {
  color: #007AFF;
}

.nav a.active:after, .nav a:hover:after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -4px;
  width: 100%;
  height: 2px;
  background: #007AFF;
  border-radius: 1px;
  transition: all 0.3s;
}

.nav a:after {
  content: '';
  position: absolute;
  left: 0;
  bottom: -4px;
  width: 0;
  height: 3px;
  background: linear-gradient(90deg, #ffe082 0%, #42b983 100%);
  border-radius: 2px;
  transition: all 0.3s;
}

.search {
  display: flex;
  position: relative;
  align-items: center;
  background: rgba(255,255,255,0.8);
  border-radius: 10px;
  box-shadow: 0 2px 10px rgba(0,0,0,0.05);
  padding: 3px 6px 3px 12px;
  transition: box-shadow 0.3s, background 0.3s;
  border: 1px solid rgba(255,255,255,0.5);
}

.search input {
  padding: 10px 15px;
  border: none;
  border-radius: 8px;
  outline: none;
  width: 240px;
  font-size: 0.95rem;
  background: rgba(255,255,255,0.7);
  box-shadow: none;
  transition: all 0.3s;
  color: #333;
}

.search input:focus {
  background: #fff;
  box-shadow: 0 0 0 3px rgba(0,122,255,0.2);
}

.search button {
  background: #007AFF;
  color: white;
  border: none;
  padding: 0 22px 0 18px;
  border-radius: 8px;
  cursor: pointer;
  font-weight: 500;
  font-size: 1rem;
  margin-left: 8px;
  box-shadow: 0 2px 8px rgba(0,122,255,0.2);
  display: flex;
  align-items: center;
  gap: 6px;
  transition: all 0.3s;
  height: 40px;
}

.search button:hover {
  transform: translateY(-2px) scale(1.04);
  box-shadow: 0 6px 18px rgba(255,255,255,0.25);
  text-shadow: 0 0 8px rgba(255,255,255,0.7);
}

.search svg {
  width: 20px;
  height: 20px;
  fill: #ffffff;
  transition: all 0.3s;
  filter: drop-shadow(0 0 2px rgba(255,255,255,0.3));
}

.search button:hover svg {
  fill: #ffffff;
  filter: drop-shadow(0 0 4px rgba(255,255,255,0.7));
}

@keyframes shine {
  0% {
    background-position: -100% 0;
  }
  100% {
    background-position: 200% 0;
  }
}

/* 搜索结果样式 */
.search-results {
  position: absolute;
  top: 110%;
  left: 0;
  width: 100%;
  background-color: rgba(255,255,255,0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.1);
  margin-top: 5px;
  z-index: 1010;
  max-height: 350px;
  overflow-y: auto;
  border: 1px solid rgba(255,255,255,0.5);
}

.search-results ul {
  list-style: none;
  padding: 0;
  margin: 0;
}

.search-results li {
  padding: 10px 15px;
  border-bottom: 1px solid #eee;
  cursor: pointer;
  transition: background-color 0.2s;
}

.search-results li:last-child {
  border-bottom: none;
}

.search-results li:hover {
  background-color: #f5f7fa;
}

.search-item {
  display: flex;
  align-items: center;
}

.search-item-image {
  width: 40px;
  height: 40px;
  margin-right: 12px;
  border-radius: 4px;
  overflow: hidden;
  background: #f0f7ff;
}

.search-item-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.search-item-info {
  flex: 1;
}

.search-item-name {
  font-weight: 500;
  color: #333;
  margin-bottom: 2px;
}

.search-item-manufacturer {
  font-size: 0.85rem;
  color: #999;
}

.loading-results,
.error-results,
.empty-results {
  padding: 20px;
  text-align: center;
  color: #999;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .header-container {
    flex-direction: column;
    padding: 10px;
  }
  
  .nav {
    margin: 15px 0;
  }
  
  .nav ul {
    flex-wrap: wrap;
    justify-content: center;
    gap: 18px;
  }
  
  .search {
    width: 100%;
    margin-top: 15px;
  }
  
  .search input {
    flex: 1;
  }
}

.logo {
  display: flex;
  align-items: center;
}

/* 用户账户区域样式 */
.user-account {
  display: flex;
  align-items: center;
}

.auth-btn {
  background: linear-gradient(135deg, #007AFF 0%, #0051d5 100%);
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(0, 122, 255, 0.2);
}

.auth-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 122, 255, 0.3);
}

.user-menu {
  position: relative;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  padding: 8px 12px;
  border-radius: 8px;
  transition: background-color 0.3s;
}

.user-info:hover {
  background-color: rgba(0, 122, 255, 0.1);
}

.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: linear-gradient(135deg, #007AFF 0%, #0051d5 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 14px;
}

.user-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
}

.dropdown-icon {
  width: 16px;
  height: 16px;
  fill: #666;
  transition: transform 0.3s;
}

.user-menu.active .dropdown-icon {
  transform: rotate(180deg);
}

.user-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: white;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  min-width: 150px;
  margin-top: 8px;
  z-index: 1001;
}

.dropdown-item {
  padding: 12px 16px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #333;
  transition: background-color 0.3s;
}

.dropdown-item:hover {
  background-color: #f5f5f5;
}

.dropdown-item svg {
  width: 16px;
  height: 16px;
  fill: #666;
}

.dropdown-item.logout {
  color: #f56c6c;
}

.dropdown-item.logout svg {
  fill: #f56c6c;
}

.dropdown-divider {
  height: 1px;
  background-color: #f0f0f0;
  margin: 4px 0;
}

/* 支付二维码弹窗 */
.payment-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
}

.payment-dialog {
  background: white;
  border-radius: 12px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.15);
  width: 90%;
  max-width: 400px;
  padding: 30px;
  position: relative;
}

.payment-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 25px;
}

.payment-header h3 {
  margin: 0;
  font-size: 18px;
  color: #333;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  transition: color 0.3s;
}

.close-btn:hover {
  color: #333;
}

.payment-content {
  text-align: center;
}

.payment-content .product-info {
  margin-bottom: 25px;
}

.payment-content h4 {
  margin: 0 0 10px 0;
  font-size: 16px;
  color: #333;
}

.payment-content .price {
  margin: 0;
  font-size: 24px;
  color: #f56c6c;
  font-weight: bold;
}

.qr-code-container {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.qr-code {
  width: 200px;
  height: 200px;
  margin-bottom: 15px;
  border-radius: 8px;
}

.qr-tip {
  margin: 0;
  font-size: 14px;
  color: #666;
}

@media (max-width: 768px) {
  .user-account {
    margin-top: 15px;
  }
  
  .auth-btn {
    width: 100%;
  }
}
</style>